<template>
  <div class="property-followup-wrapper">
    <!-- Loading Component for save operations -->
    <IsLoadingComponent v-if="showLoading" />

    <!-- Form Content (disabled while loading) -->
    <div :class="{ 'form-disabled': showLoading }">
      <!-- Selected Assignment and Email Section -->
      <SelectedEmailAndAssignment :emailData="emailData" class="mb-4" />

      <label class="field-label checkbox-main-label">Assignments</label>
      <div class="checkbox-description">
        Select an assignment that has been sent a Property Contact Request to follow up
      </div>

      <!-- Loading indicator for assignments -->
      <div v-if="isLoadingAssignments" class="loading-assignments">
        <div class="loading-text">Loading eligible assignments...</div>
      </div>

      <AssignmentList
        v-else
        ref="assignmentList"
        :loadOnMount="false"
        :autoExtractFromEmail="false"
        :customAssignments="eligibleAssignments"
        :externalLoadingMore="isLoadingMore"
        :externalHasMore="hasMoreAssignments"
        :selectedAssignment="localSelectedAssignment"
        :showCreatedDate="true"
        context="propertyFollowUp"
        @select="handleAssignmentSelect"
        @loadMore="loadMoreAssignments"
        @refresh="handleRefresh"
        @showNotification="handleNotification"
      />

      <!-- Reply to Selected Email Checkbox -->
      <div class="form-field reply-checkbox-section">
        <div class="reply-checkbox-container">
          <v-checkbox
            v-model="formData.replyToSelectedEmail"
            color="#003f2d"
            hide-details
            class="reply-checkbox"
          />
          <label class="field-label checkbox-main-label">Reply to Selected Email</label>
        </div>
        <div class="checkbox-description">
          Replying to the selected email will keep the emails subject and recipient list
        </div>
      </div>

      <!-- Follow Up Statement Selector -->
      <FollowUpStatementSelector
        v-model="formData.selectedFollowUpStatement"
        context="property"
        @statement-changed="handleFollowUpStatementChanged"
        @success="handleFollowUpStatementSuccess"
        @error="handleFollowUpStatementError"
      />

      <!-- Additional Recipients Section - Only show when NOT replying to selected email -->
      <AdditionalRecipientsStandalone
        v-if="!formData.replyToSelectedEmail"
        ref="recipientsComponent"
        :assignment="localSelectedAssignment"
        context="propertyFollowUp"
        @selection-changed="handleRecipientSelectionChanged"
      />

      <!-- Signature Field -->
      <div class="form-field">
        <label class="field-label">Signature*</label>
        <div class="signature-container">
          <VueEditor
            ref="signatureEditor"
            key="signatureEditor"
            class="signature-vue-editor"
            v-model="formData.signature"
            :editorToolbar="customToolbar"
            :editorOptions="editorOptions"
            placeholder="Enter your signature here..."
            @ready="onEditorReady"
            @input="onSignatureInput"
            @text-change="onSignatureTextChange"
          />
          <div class="signature-char-count" :class="{ error: signatureLength > 500 }">
            {{ signatureLength }}/500 characters
          </div>
          <div v-if="formErrors.signature" class="error-message">
            {{ formErrors.signature }}
          </div>
        </div>
      </div>
    </div>
    <!-- End form-disabled wrapper -->
  </div>
  <!-- End property-followup-wrapper -->
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DateComponent from "../commons/DateComponent.vue";
import TimePickerComponent from "../commons/TimePickerComponent.vue";
import SelectedEmailAndAssignment from "../commons/SelectedEmailAndAssignment.vue";
import IsLoadingComponent from "../IsLoadingComponent.vue";
import FollowUpStatementSelector from "../common/FollowUpStatementSelector.vue";
import AssignmentList from "../actions/assignment/AssignmentList.vue";
import AdditionalRecipientsStandalone from "./AdditionalRecipientsStandalone.vue";
import { VueEditor } from "vue3-editor";
import { generateEmailTemplate, EMAIL_TEMPLATES } from "../../service/emailTemplates.js";
import { getGraphToken } from "@/service/TokenService.js";
import { ACTION_TYPES } from "../../constants.js";
import {
  handleRecentActions,
  fetchPropertyFollowUpEligibleAssignments,
  clearAssignmentCache,
} from "@/service/engagement.js";
import { markEmailAsCompleteNew } from "../../service/Actions.js";
import { addProcessingTime } from "../../utils/metricsHelper.js";
import { getPropertyAddressOrName } from "@/utils/globalUtil.js";
import moment from "moment";

export default {
  name: "PropertyFollowUp",
  components: {
    DateComponent,
    TimePickerComponent,
    SelectedEmailAndAssignment,
    IsLoadingComponent,
    FollowUpStatementSelector,
    AssignmentList,
    AdditionalRecipientsStandalone,
    VueEditor,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    actionData: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      formData: {
        replyToSelectedEmail: true,
        selectedFollowUpStatement: "default",
        selectedFollowUpStatementObject: null,
        signature: "",
      },
      formErrors: {
        signature: "",
      },
      showLoading: false,
      processingStartTime: null,
      selectedRecipients: [],
      localSelectedAssignment: null,
      eligibleAssignments: [],
      currentPage: 0,
      pageSize: 5,
      hasMoreAssignments: true,
      isLoadingAssignments: false,
      isLoadingMore: false,
      isLoadingSignature: false,
      isSignatureFocused: false,
      customToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
      ],
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
          ],
        },
        theme: "snow",
        strict: false,
        scrollingContainer: null,
        bounds: null,
        readOnly: false,
        placeholder: "Enter your signature here...",
        formats: ["bold", "italic", "underline", "link", "list"],
      },
    };
  },
  created() {
    this.$store.dispatch("lookup/fetchAllLookups");
  },
  mounted() {
    // Emit initial validation state
    this.$emit("isValidForSubmit", this.isFormValid);
  },
  computed: {
    ...mapGetters({
      emailData: "email/currentEmail",
      getUserDetails: "email/getUserDetails",
    }),

    assignmentDateRange() {
      if (!this.localSelectedAssignment) return "";

      const createdDate =
        this.localSelectedAssignment.createdTimestamp ||
        this.localSelectedAssignment.createdDate;
      const dueDate =
        this.localSelectedAssignment.deadline?.assignmentDueDate ||
        this.localSelectedAssignment.assignmentDueDate;

      if (!createdDate || !dueDate) return "";

      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        try {
          return moment(dateStr).format("MM/DD/YYYY");
        } catch {
          return "";
        }
      };

      return `Assignment Date Range: ${formatDate(createdDate)} - ${formatDate(dueDate)}`;
    },

    hasEmailForReply() {
      const hasEmail = this.emailData && this.emailData.subject && this.emailData.itemId;
      return hasEmail;
    },

    signatureLength() {
      if (!this.formData.signature) return 0;
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = this.formData.signature || "";
      return tempDiv.textContent.trim().length;
    },

    isFormValid() {
      const { signature } = this.formData;

      // Enhanced signature validation (required) - check for meaningful content
      let signatureValid = false;
      if (signature && signature.trim()) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = signature;
        const textContent = tempDiv.textContent.trim();
        signatureValid = textContent.length > 0;
      }

      return (
        this.localSelectedAssignment !== null &&
        signatureValid &&
        !this.formErrors.signature
      );
    },
  },
  watch: {
    visible: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.processingStartTime = Date.now();
          await this.initializeForm();
        } else {
          this.resetForm();
        }
      },
    },
    isFormValid: {
      immediate: true,
      handler(newVal) {
        this.$emit("isValidForSubmit", newVal);
      },
    },
  },
  methods: {
    async initializeForm() {
      try {
        // Reset local selection when dialog opens
        this.localSelectedAssignment = null;
        // Reset follow-up statement to default
        this.formData.selectedFollowUpStatement = "default";
        this.formData.selectedFollowUpStatementObject = null;
        // Reset selected recipients
        this.selectedRecipients = [];

        // Use nextTick to ensure component is mounted before calling resetState
        await this.$nextTick();

        // Reset recipients component if it exists
        if (
          this.$refs.recipientsComponent &&
          typeof this.$refs.recipientsComponent.resetState === "function"
        ) {
          this.$refs.recipientsComponent.resetState();
        }

        // Fetch eligible assignments
        await this.fetchEligibleAssignments();
      } catch (error) {
        console.error("❌ Error initializing form:", error);
      }
    },

    async fetchEligibleAssignments() {
      try {
        this.isLoadingAssignments = true;
        this.currentPage = 0;
        this.eligibleAssignments = [];
        this.hasMoreAssignments = true;

        // Explicitly clear assignment to trigger component reset
        this.localSelectedAssignment = null;

        const userDetails = this.getUserDetails;
        const userEmail = userDetails?.emailAddress;

        if (!userEmail) {
          this.$emit("showNotification", {
            type: "error",
            message: "Unable to fetch assignments: User email not found",
          });
          return;
        }

        // Load first page
        await this.loadMoreAssignments();

        // Auto-select: if the TaskPane's currently selected assignment exists,
        // always ensure it appears at the top of the list and pre-select it.
        // This handles the case where the assignment is on a later paginated page
        // and would not be visible on initial load.
        const mainSelected = this.$store.getters['assignments/selectedAssignment'];
        if (mainSelected && !this.localSelectedAssignment) {
          const matchIndex = this.eligibleAssignments.findIndex(
            (a) =>
              (mainSelected.viewEventId && a.viewEventId === mainSelected.viewEventId) ||
              (mainSelected.code && a.code === mainSelected.code) ||
              (mainSelected.id && a.id === mainSelected.id)
          );

          if (matchIndex > 0) {
            // Found but not at top — move it to position 0
            const [match] = this.eligibleAssignments.splice(matchIndex, 1);
            this.eligibleAssignments.unshift(match);
            await this.handleAssignmentSelect(match);
          } else if (matchIndex === 0) {
            // Already at top
            await this.handleAssignmentSelect(this.eligibleAssignments[0]);
          } else {
            // Not found on first page (it's on a later page) — pin it at the top
            // using the store object so it's immediately visible and selectable.
            const pinnedAssignment = {
              id: mainSelected.id,
              code: mainSelected.code || mainSelected.fileId,
              viewEventId: mainSelected.viewEventId,
              trackerEngagementStatus: mainSelected.trackerEngagementStatus,
              propertyName: mainSelected.propertyName || mainSelected.propertyAddress?.propertyName,
              propertyAddress: mainSelected.propertyAddress,
              address: mainSelected.address,
              client: mainSelected.client,
              clientDetails: mainSelected.clientDetails,
              primaryValuer: mainSelected.primaryValuer,
              primaryValuerName: mainSelected.primaryValuerName || mainSelected.primaryValuer?.fullName,
              originator: mainSelected.originator,
              deadline: mainSelected.deadline,
              createdTimestamp: mainSelected.createdTimestamp,
              draftDeliveredDate: mainSelected.draftDeliveredDate,
              viewEventId: mainSelected.viewEventId,
              sourceSystemId: mainSelected.sourceSystemId,
              userFriendlyId: mainSelected.userFriendlyId,
              _pinnedFromStore: true, // marker so we know this was injected
            };
            this.eligibleAssignments.unshift(pinnedAssignment);
            await this.handleAssignmentSelect(pinnedAssignment);
          }
        }
      } catch (error) {
        this.$emit("showNotification", {
          type: "error",
          message: "Failed to fetch eligible assignments",
        });
        this.eligibleAssignments = [];
      } finally {
        this.isLoadingAssignments = false;
      }
    },

    async loadMoreAssignments() {
      if (this.isLoadingMore || (!this.hasMoreAssignments && this.currentPage > 0)) {
        return;
      }

      this.isLoadingMore = true;

      try {
        const userDetails = this.getUserDetails;
        const userEmail = userDetails?.emailAddress;

        if (!userEmail) {
          this.hasMoreAssignments = false;
          return;
        }

        let accumulatedAssignments = [];
        let keepFetching = true;
        let fetchAttempts = 0;
        const MAX_FETCH_ATTEMPTS = 5; // Safety limit to prevent infinite loops

        // Continue fetching until we have enough valid items or run out of data
        while (keepFetching && fetchAttempts < MAX_FETCH_ATTEMPTS) {
          fetchAttempts++;

          // Fetch assignments for this page from backend
          let response = await fetchPropertyFollowUpEligibleAssignments(
            userEmail,
            this.currentPage,
            this.pageSize
          );

          // Handle both old (array) and new ({assignments, hasMore}) response format for safety
          let pageAssignments = [];
          let pageHasMore = false;

          if (Array.isArray(response)) {
            pageAssignments = response;
            pageHasMore = pageAssignments.length === this.pageSize;
          } else if (response && typeof response === "object") {
            pageAssignments = response.assignments || [];
            pageHasMore = !!response.hasMore;
          }

          // Advance page for next iteration regardless of validation
          this.currentPage++;
          this.hasMoreAssignments = pageHasMore;

          // Filter out Billing status (id: 5) and Completed status (id: 6) locally to ensure we display valid items
          const validAssignments = (pageAssignments || []).filter(
            (a) => a.trackerEngagementStatus?.id !== 5 && a.trackerEngagementStatus?.id !== 6
          );

          accumulatedAssignments = [...accumulatedAssignments, ...validAssignments];

          // Stop if we have gathered enough items to show a full "page"
          // Or if the backend has no more items to serve
          if (accumulatedAssignments.length >= this.pageSize || !pageHasMore) {
            keepFetching = false;
          }
        }

        if (accumulatedAssignments.length > 0) {
          // Deduplicate against what's already in the list (e.g. a pinned assignment
          // at the top that was injected from the store before this page arrived)
          const existingIds = new Set(
            this.eligibleAssignments.map((a) => a.viewEventId || a.code || a.id).filter(Boolean)
          );
          const deduped = accumulatedAssignments.filter(
            (a) => !existingIds.has(a.viewEventId || a.code || a.id)
          );
          this.eligibleAssignments = [...this.eligibleAssignments, ...deduped];
        }
      } catch (error) {
        this.$emit("showNotification", {
          type: "error",
          message: "Failed to load more assignments",
        });
        this.hasMoreAssignments = false;
      } finally {
        this.isLoadingMore = false;
        this.isLoadingAssignments = false;
      }
    },

    async handleAssignmentSelect(assignment) {
      this.localSelectedAssignment = assignment;
      this.$emit("select", assignment);

      // ALWAYS fetch the absolute latest assignment data from server (not cache)
      if (assignment && assignment.viewEventId) {
        try {
          // CRITICAL: Clear cache FIRST to ensure we get fresh data from server
          clearAssignmentCache(assignment.viewEventId);
          
          // Force fresh fetch from server
          await this.$store.dispatch(
            "assignments/fetchAssignmentOverviewData",
            assignment.viewEventId
          );

          // Wait for data to be available in store
          await this.$nextTick();
          
          // Get the freshly fetched assignment data from store
          const enrichedAssignment = this.$store.getters[
            "assignments/assignmentOverviewData"
          ];
          
          if (
            enrichedAssignment &&
            enrichedAssignment.valuationAssignmentInformation?.propertyContacts
          ) {
            // Merge FRESH propertyContacts into localSelectedAssignment
            this.localSelectedAssignment = {
              ...this.localSelectedAssignment,
              propertyContacts:
                enrichedAssignment.valuationAssignmentInformation.propertyContacts,
            };
          }
        } catch (error) {
          console.error("❌ Error fetching latest assignment overview data:", error);
          this.$emit("showNotification", {
            type: "warning",
            message: "Could not load latest property contact details. Using cached data."
          });
        }
      }

      // Emit validation state after assignment selection
      this.$nextTick(() => {
        this.$emit("isValidForSubmit", this.isFormValid);
      });
    },

    handleRefresh() {
      this.fetchEligibleAssignments();
    },

    handleFollowUpStatementChanged(statement) {
      this.formData.selectedFollowUpStatementObject = statement;
      // Emit validation state after statement change
      this.$nextTick(() => {
        this.$emit("isValidForSubmit", this.isFormValid);
      });
    },

    handleFollowUpStatementSuccess(message) {
      if (message) {
        this.$emit("showNotification", {
          type: "success",
          message: message,
        });
      }
    },

    handleFollowUpStatementError(message) {
      console.error("❌ Follow up statement error:", message);
      if (message) {
        this.$emit("showNotification", {
          type: "error",
          message: message,
        });
      }
    },

    handleRecipientSelectionChanged(recipients) {
      // AdditionalRecipientsStandalone emits { checkedCC, selectedEmails }
      if (recipients && recipients.selectedEmails) {
        // Convert email array to recipient objects with name and email
        this.selectedRecipients = recipients.selectedEmails.map((email) => ({
          email: email,
          name: email.split("@")[0], // Use email prefix as fallback name
        }));
      } else {
        this.selectedRecipients = [];
      }
    },

    handleNotification(notification) {
      this.$emit("showNotification", notification);
    },

    async startTemplate() {
      // Set loading state
      this.showLoading = true;

      try {
        // Enhanced signature validation with HTML content check
        if (!this.formData.signature || !this.formData.signature.trim()) {
          this.formErrors.signature = "Signature is required";
        } else {
          // Check for meaningful text content (not just HTML tags)
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = this.formData.signature;
          const textContent = (tempDiv.textContent || tempDiv.innerText || "").trim();

          if (textContent.length === 0) {
            this.formErrors.signature = "Signature cannot be empty";
          } else if (this.signatureLength > 500) {
            this.formErrors.signature =
              "Signature is too long. Please keep it under 500 characters.";
          } else {
            this.formErrors.signature = "";
          }
        }

        if (!this.isFormValid) {
          let errorMessage = "Please fill in all required fields";

          // Provide specific error messages for mandatory fields
          if (!this.localSelectedAssignment) {
            errorMessage = "Please select an assignment";
          } else if (!this.formData.signature || !this.formData.signature.trim()) {
            errorMessage = "Please enter your signature";
          } else if (this.formErrors.signature) {
            errorMessage = this.formErrors.signature;
          }

          this.$emit("showNotification", {
            type: "error",
            message: errorMessage,
          });
          return;
        }

        // Create email template
        await this.createPropertyFollowUpEmail();
      } catch (error) {
        console.error("❌ Error creating property follow up email:", error);

        this.$emit("showNotification", {
          type: "error",
          message:
            error.message ||
            "Failed to create property follow up email. Please try again.",
        });
      } finally {
        // Always clear loading state
        this.showLoading = false;
      }
    },

    async createPropertyFollowUpEmail() {
      if (!this.isFormValid) {
        this.$emit("showNotification", {
          type: "error",
          message: "Please fill in all required fields",
        });
        return;
      }

      let finalStatus = "Completed";
      let errorMessage = null;
      const metricsPayload = {
        entityId: this.localSelectedAssignment?.viewEventId,
        fileId: this.localSelectedAssignment?.code,
        actionTaken: ACTION_TYPES.PROPERTY_CONTACT_FOLLOW_UP,
      };

      // Declare variables at method scope so they're accessible in finally block
      let propertyName = "Property";
      let propertyAddressString = "";
      let propertyContactName = "";
      let propertyContactEmail = "";
      let emailContent = null;

      try {
        // CRITICAL: Before creating email, fetch the ABSOLUTE LATEST property contact data
        if (this.localSelectedAssignment && this.localSelectedAssignment.viewEventId) {
          try {
            // Clear cache to force fresh fetch
            clearAssignmentCache(this.localSelectedAssignment.viewEventId);
            
            // Fetch latest data from server
            await this.$store.dispatch(
              "assignments/fetchAssignmentOverviewData",
              this.localSelectedAssignment.viewEventId
            );
            
            // Wait for store to update
            await this.$nextTick();
            
            // Get the freshest data from store
            const latestAssignmentData = this.$store.getters[
              "assignments/assignmentOverviewData"
            ];
            
            if (
              latestAssignmentData &&
              latestAssignmentData.valuationAssignmentInformation?.propertyContacts
            ) {
              // Update local assignment with FRESH property contacts
              this.localSelectedAssignment = {
                ...this.localSelectedAssignment,
                propertyContacts:
                  latestAssignmentData.valuationAssignmentInformation.propertyContacts,
              };
              
            }
          } catch (refreshError) {
            console.warn('⚠️ Could not refresh property contact data, using existing:', refreshError);
          }
        }

        // 1. Determine Property Name, Address, and Contact Info
        if (this.localSelectedAssignment) {
          // Get property name and address
           propertyAddressString = getPropertyAddressOrName(this.localSelectedAssignment, false) || '';
           propertyName = getPropertyAddressOrName(this.localSelectedAssignment, true) || 'Property';

          // Get property contact information - STRICT RULE: Use only propertyContactEmail and propertyContactName
          const propertyContacts = this.localSelectedAssignment.propertyContacts;
          if (propertyContacts) {
            // Handle if propertyContacts is an array or object
            if (Array.isArray(propertyContacts) && propertyContacts.length > 0) {
              const firstContact = propertyContacts[0];
              propertyContactName = firstContact.propertyContactName || "";
              propertyContactEmail = firstContact.propertyContactEmail || "";
            } else if (
              typeof propertyContacts === "object" &&
              propertyContacts !== null &&
              !Array.isArray(propertyContacts)
            ) {
              propertyContactName = propertyContacts.propertyContactName || "";
              propertyContactEmail = propertyContacts.propertyContactEmail || "";
            }
          }

          // If still no contact found, try assignmentContract
          if (!propertyContactEmail || propertyContactEmail.trim() === "") {
            const assignmentContract = this.localSelectedAssignment.assignmentContract;
            if (assignmentContract) {
              const contractName = assignmentContract.propertyContactName;
              const contractEmail = assignmentContract.propertyContactEmail;

              if (contractEmail && contractEmail.trim()) {
                propertyContactName = contractName || "";
                propertyContactEmail = contractEmail;
              }
            }
          }

          // If still no contact found, try valuationAssignmentInformation.propertyContacts
          if (!propertyContactEmail || propertyContactEmail.trim() === "") {
            const contactInfo = this.localSelectedAssignment
              .valuationAssignmentInformation?.propertyContacts;
            if (contactInfo) {
              if (Array.isArray(contactInfo) && contactInfo.length > 0) {
                const info = contactInfo[0];
                propertyContactName = info.propertyContactName || "";
                propertyContactEmail = info.propertyContactEmail || "";
              } else if (
                typeof contactInfo === "object" &&
                contactInfo !== null &&
                !Array.isArray(contactInfo)
              ) {
                propertyContactName = contactInfo.propertyContactName || "";
                propertyContactEmail = contactInfo.propertyContactEmail || "";
              }
            }
          }

          // Final fallback: Direct fields on assignment
          if (!propertyContactEmail || propertyContactEmail.trim() === "") {
            if (this.localSelectedAssignment.propertyContact) {
              propertyContactName = this.localSelectedAssignment.propertyContact;
            }
            if (this.localSelectedAssignment.propertyContactEmail) {
              propertyContactEmail = this.localSelectedAssignment.propertyContactEmail;
            }
          }
        }

        // 2. Generate Email from Template
        const userInfo = this.getUserInfo();
        const selectedFollowUpStatement = this.formData.selectedFollowUpStatementObject;
        const useDefaultIntro =
          !selectedFollowUpStatement || selectedFollowUpStatement.id === "default";
        const introContent =
          selectedFollowUpStatement?.statement ||
          selectedFollowUpStatement?.body ||
          selectedFollowUpStatement?.content || "";
        const isNewEmail = !this.formData.replyToSelectedEmail;
        const contactFirstName = propertyContactName?.split(" ")?.[0] || "";

        const dynamicData = {
          fileId: this.localSelectedAssignment?.code || "",
          propertyContactFirstName: contactFirstName,
          propertyAddress: propertyAddressString,
          propertyName: propertyName,
          isNewEmail: isNewEmail,
          useDefaultIntro: useDefaultIntro,
          introContent: introContent,
          signature: this.formData.signature || "",
        };

        const accessToken = await getGraphToken();
        emailContent = await generateEmailTemplate(
          "Property Follow Up",
          userInfo,
          dynamicData,
          accessToken
        );

        // 3. Prepare Signature - only if NOT replying to selected email
        let fullHtmlBody = emailContent.htmlBody;

        if (!this.formData.replyToSelectedEmail) {
          const signatureHtml = this.formData.signature || "";

          // Check for meaningful signature content (not just empty HTML tags)
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = signatureHtml;
          const signatureText = tempDiv.textContent || tempDiv.innerText || "";

          let cleanedSignature = "";
          if (signatureHtml.trim() && signatureText.trim()) {
            // Save signature to roaming settings when it's being used
            try {
              await this.saveSignatureForFutureUse(signatureHtml);
            } catch (saveError) {
              console.warn("Could not save signature:", saveError);
            }

            // Use the method that preserves rich text formatting
            cleanedSignature = this.preserveRichTextFormatting(signatureHtml);
          }

          // 4. Combine body and signature with proper spacing (only for new emails)
          fullHtmlBody = cleanedSignature
            ? `${emailContent.htmlBody}<br/>${cleanedSignature}`
            : emailContent.htmlBody;
        }

        // 5. Prepare CC Recipients
        const ccRecipients =
          Array.isArray(this.selectedRecipients) && this.selectedRecipients.length > 0
            ? this.selectedRecipients.map((r) => {
                // Handle both formats: { email, name } or just email string
                if (typeof r === "string") {
                  return {
                    emailAddress: r,
                    displayName: r.split("@")[0],
                  };
                }
                return {
                  emailAddress: r.email || r.emailAddress,
                  displayName:
                    r.name || r.displayName || (r.email || r.emailAddress)?.split("@")[0],
                };
              })
            : [];

        // 6. Create email based on reply/new selection
        if (
          this.formData.replyToSelectedEmail &&
          this.emailData &&
          this.emailData.subject &&
          window.Office?.context?.mailbox?.item
        ) {
          // Reply to selected email using displayReplyAllForm (signature will be added by Outlook)
          this.createReplyToSelectedEmail(fullHtmlBody);
        } else {
          // Create new email with programmatic signature
          const subject = emailContent.subject;

          // Prepare TO recipients with property contact
          const toRecipients = [];
          if (propertyContactEmail) {
            toRecipients.push({
              emailAddress: propertyContactEmail,
              displayName: propertyContactName,
            });
          }

          Office.context.mailbox.displayNewMessageForm({
            toRecipients: toRecipients,
            ccRecipients: ccRecipients,
            subject: subject,
            htmlBody: fullHtmlBody,
          });

          // 7. Mark email as complete IMMEDIATELY after opening the form (Performance Optimization)
          // Moving this specific call before handleRecentActions ensures the "tick" appears fast
          if (this.config?.processingOptions?.markAsProcessed?.default) {
            try {
              await markEmailAsCompleteNew(
                "property-followup-complete",
                "Property Contact Follow Up email created successfully",
                undefined,
                true
              );
            } catch (markError) {
              console.error("Failed to mark email as complete", markError);
              // Proceed without blocking the rest of the flow
            }
          }

          // 8. Track action in recent actions
          if (this.localSelectedAssignment) {
            const payload = {
              entityIds: [this.localSelectedAssignment.viewEventId],
              fileId: this.localSelectedAssignment.code,
              subjectLine: subject, // Use the subject variable created above for new emails
              toAddress: Array.isArray(this.selectedRecipients)
                ? this.selectedRecipients.map((r) => r.email).join(", ")
                : "",
            };

            await handleRecentActions(payload, ACTION_TYPES.PROPERTY_CONTACT_FOLLOW_UP);
          }
        }

        // For reply case, mark as complete and track action here (after reply is created)
        if (this.formData.replyToSelectedEmail) {
          // Mark email as complete for reply case
          if (this.config?.processingOptions?.markAsProcessed?.default) {
            try {
              await markEmailAsCompleteNew(
                "property-followup-complete",
                "Property Contact Follow Up email created successfully",
                undefined,
                true
              );
            } catch (markError) {
              console.error("Failed to mark email as complete", markError);
            }
          }

          // Track action for reply case
          if (this.localSelectedAssignment) {
            const replySubject = this.createReplySubject(this.emailData.subject);
            const payload = {
              entityIds: [this.localSelectedAssignment.viewEventId],
              fileId: this.localSelectedAssignment.code,
              subjectLine: replySubject, // Use proper reply subject
              toAddress: Array.isArray(this.selectedRecipients)
                ? this.selectedRecipients.map((r) => r.email).join(", ")
                : "",
            };

            await handleRecentActions(payload, ACTION_TYPES.PROPERTY_CONTACT_FOLLOW_UP);
          }
        }

        this.$emit("showNotification", {
          type: "success",
          message: "Property Contact Follow Up email created successfully",
        });

        // Clear the local assignment selection after successful action
        this.localSelectedAssignment = null;

        // Close the dialog
        this.$emit("close");
      } catch (error) {
        finalStatus = "Error";
        errorMessage = error.message;
        console.error("❌ Error creating property follow up email:", error);
        this.$emit("showNotification", {
          type: "error",
          message:
            error.message ||
            "Failed to create property follow up email. Please try again.",
        });
      } finally {
        // Add comprehensive metrics
        const endTime = performance.now();
        const manualProcessingTime = Math.round(endTime - this.processingStartTime);

        const explicitPayload = {
          entityId: metricsPayload.entityId,
          fileId: metricsPayload.fileId,
          actionTaken: metricsPayload.actionTaken,
          status: finalStatus,
          subjectLine:
            this.formData.replyToSelectedEmail && this.emailData?.subject
              ? this.createReplySubject(this.emailData.subject)
              : emailContent?.subject || `Follow Up for ${propertyName}${propertyAddressString ? ' | ' + propertyAddressString : ''}`,
          processingTime: Math.max(0, manualProcessingTime),
          timestamp: new Date().toISOString(),
          toAddress: Array.isArray(this.selectedRecipients)
            ? this.selectedRecipients.map((r) => r.email).join(", ")
            : "",
        };

        if (errorMessage) {
          explicitPayload.errorMessage = errorMessage;
        }

        // Ensure processingTime is always a valid number
        if (
          explicitPayload.processingTime == null ||
          isNaN(explicitPayload.processingTime)
        ) {
          explicitPayload.processingTime = 0;
        }

        await this.$store.dispatch("assignments/addMetricsData", explicitPayload);
      }
    },

    getUserInfo() {
      try {
        return {
          email: Office.context.mailbox.userProfile.emailAddress || "Unknown",
          name: Office.context.mailbox.userProfile.displayName || "User",
        };
      } catch (error) {
        return {
          email: "Unknown",
          name: "User",
        };
      }
    },

    resetForm() {
      this.formData = {
        replyToSelectedEmail: false,
        selectedFollowUpStatement: "default",
        selectedFollowUpStatementObject: null,
        signature: "",
      };
      this.formErrors = {
        signature: "",
      };
      this.selectedRecipients = [];
      this.processingStartTime = null;
      this.localSelectedAssignment = null;

      // Reset recipients component if it exists
      if (
        this.$refs.recipientsComponent &&
        typeof this.$refs.recipientsComponent.resetState === "function"
      ) {
        this.$refs.recipientsComponent.resetState();
      }
    },

    onEditorReady(quill) {
      if (quill) {
        const originalGetBounds = quill.selection?.getBounds;
        if (originalGetBounds) {
          quill.selection.getBounds = function (index, length) {
            try {
              return originalGetBounds.call(this, index, length);
            } catch (err) {
              return { top: 0, left: 0, height: 0, width: 0 };
            }
          };
        }
      }
      this.loadSignatureFromSettings();
    },

    onSignatureInput(content) {
      this.formData.signature = content || "";

      // Auto-save signature when changed
      if (this.formData.signature && this.formData.signature.trim()) {
        this.saveSignatureForFutureUse(this.formData.signature);
      }

      // Enhanced validation for signature
      if (content && content.trim()) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent.trim();
        if (textContent.length === 0) {
          this.formErrors.signature = "Signature cannot be empty";
        } else if (textContent.length > 500) {
          this.formErrors.signature =
            "Signature is too long. Please keep it under 500 characters.";
        } else {
          this.formErrors.signature = "";
        }
      } else {
        this.formErrors.signature = "Signature is required";
      }
    },

    onSignatureTextChange(delta, oldDelta, source) {
      const content = this.$refs.signatureEditor?.quill?.root?.innerHTML;
      if (content !== undefined) {
        this.formData.signature = content;
      }

      // Enhanced validation for signature
      if (this.formData.signature && this.formData.signature.trim()) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = this.formData.signature;
        const textContent = tempDiv.textContent.trim();
        if (textContent.length === 0) {
          this.formErrors.signature = "Signature cannot be empty";
        } else if (textContent.length > 500) {
          this.formErrors.signature =
            "Signature is too long. Please keep it under 500 characters.";
        } else {
          this.formErrors.signature = "";
        }
      } else {
        this.formErrors.signature = "Signature is required";
      }
    },

    async loadSignatureFromSettings() {
      try {
        const storageKey = `userSignature_${
          this.getUserDetails?.emailAddress || "default"
        }`;

        // Try roaming settings first
        if (window.Office?.context?.roamingSettings) {
          const savedSignature = window.Office.context.roamingSettings.get(
            "userSignature"
          );
          if (savedSignature) {
            this.formData.signature = savedSignature;
            return;
          }
        }

        // Fallback to localStorage
        const localSignature = localStorage.getItem(storageKey);
        if (localSignature) {
          this.formData.signature = localSignature;
        }
      } catch (error) {
        console.warn("Could not load signature:", error);
      }
    },

    async saveSignatureForFutureUse(signature) {
      try {
        const storageKey = `userSignature_${
          this.getUserDetails?.emailAddress || "default"
        }`;

        // Save to roaming settings
        if (window.Office?.context?.roamingSettings) {
          window.Office.context.roamingSettings.set("userSignature", signature);
          window.Office.context.roamingSettings.saveAsync();
        }

        // Save to localStorage as backup
        localStorage.setItem(storageKey, signature);
      } catch (error) {
        console.warn("Could not save signature:", error);
      }
    },

    // Preserve rich text formatting specifically for email
    preserveRichTextFormatting(signatureHtml) {
      // If it's plain text, just add br tags
      if (!signatureHtml.includes("<") && !signatureHtml.includes(">")) {
        return signatureHtml.replace(/\n/g, "<br>");
      }

      // Clean up the HTML while preserving all formatting tags
      let cleaned = signatureHtml
        // First, handle div to div transitions with br tags
        .replace(/<\/div>\s*<div[^>]*>/gi, "</div><br><div>")
        // Then remove all div tags but keep the br tags we just added
        .replace(/<div[^>]*>/gi, "")
        .replace(/<\/div>/gi, "")
        // Handle p tags the same way
        .replace(/<\/p>\s*<p[^>]*>/gi, "</p><br><p>")
        .replace(/<p[^>]*>/gi, "")
        .replace(/<\/p>/gi, "")
        // Convert any remaining newlines to br tags
        .replace(/\n/g, "<br>")
        // Clean up multiple consecutive br tags
        .replace(/(<br>\s*){2,}/gi, "<br>")
        // Remove trailing br
        .replace(/<br>\s*$/, "")
        // Clean up whitespace
        .replace(/\s+/g, " ")
        .trim();

      return cleaned;
    },

    // Helper method to create proper reply subjects
    createReplySubject(originalSubject) {
      if (!originalSubject || !originalSubject.trim()) {
        return "RE: ";
      }
      // Remove any existing RE: or FW: prefixes to get the clean subject
      let cleanSubject = originalSubject.trim();
      // Remove RE:, FW:, Fwd:, etc. prefixes (case insensitive)
      cleanSubject = cleanSubject.replace(/^(RE:|FW:|FWD:|Fwd:)\s*/i, "").trim();
      // Also handle multiple nested prefixes like "RE: FW: RE: original subject"
      while (/^(RE:|FW:|FWD:|Fwd:)\s*/i.test(cleanSubject)) {
        cleanSubject = cleanSubject.replace(/^(RE:|FW:|FWD:|Fwd:)\s*/i, "").trim();
      }
      // Return with proper RE: prefix
      return `RE: ${cleanSubject}`;
    },

    createReplyToSelectedEmail(htmlBody) {
      try {
        // Get the current email item
        const item = Office.context.mailbox.item;

        // Create reply with template content using displayReplyAllForm
        // This preserves the original TO addresses from the selected email
        item.displayReplyAllForm({
          htmlBody: htmlBody,
        });

        // Show success notification
        this.$emit("showNotification", {
          type: "info",
          message: "Reply created, Please add additional recipients!",
        });
      } catch (error) {
        console.error("Failed to create reply:", error);

        // Fallback to creating a new message
        const subject = this.emailData?.subject
          ? this.createReplySubject(this.emailData.subject)
          : "Property Contact Follow Up";

        Office.context.mailbox.displayNewMessageForm({
          toRecipients: [],
          ccRecipients: [],
          subject: subject,
          htmlBody: htmlBody,
        });

        this.$emit("showNotification", {
          type: "warning",
          message: "Created new email instead (reply not available)",
        });
      }
    },

    getPropertyAddressOrName,
  },
};
</script>

<style scoped>
.v-col-6 {
  flex: 1 0 45% !important;
}
/* Prevent browser autocomplete dropdowns */
.property-followup-wrapper input:-webkit-autofill,
.property-followup-wrapper input:-webkit-autofill:hover,
.property-followup-wrapper input:-webkit-autofill:focus,
.property-followup-wrapper input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
  -webkit-text-fill-color: #000 !important;
  transition: background-color 5000s ease-in-out 0s;
}

/* Disable autocomplete dropdown suggestions */
.property-followup-wrapper
  input[autocomplete="new-password"]::-webkit-contacts-auto-fill-button,
.property-followup-wrapper
  input[autocomplete="new-password"]::-webkit-credentials-auto-fill-button {
  display: none !important;
}

/* Main Container - match Route to Admin styling */
.property-followup-wrapper {
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
  padding: 0;
  position: relative; /* For loading overlay positioning */
}

/* Form disabled state while loading */
.form-disabled {
  pointer-events: none;
  opacity: 0.6;
  user-select: none;
}

/* Reset any inherited flex properties that might interfere */
.property-followup-wrapper * {
  box-sizing: border-box;
}

.property-followup-wrapper .form-field {
  display: block !important;
  width: 100% !important;
}

/* Selected Section - Based on existing patterns */
.selected-info {
  margin-bottom: 16px;
}

.value {
  color: #333;
  margin-right: 24px;
  font-weight: 400;
}

/* Form Fields - Match Route to Admin styling */
.form-field,
.field-group {
  margin-bottom: 16px;
  width: 100%;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 16px;
  color: #000;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
}

.date-range-wrapper {
  margin-bottom: 20px;
}

.template-selection {
  margin-bottom: 16px;
}

/* Selected item display styling - grayed out appearance */
.selected-item-display {
  background-color: #f5f5f5 !important;
  color: #999 !important;
  font-style: italic !important;
  pointer-events: none !important;
}

.selected-item-display:hover {
  background-color: #f5f5f5 !important;
}

/* No data available styling */
.no-data-item {
  color: #666 !important;
  text-align: center !important;
  pointer-events: none !important;
}

.no-data-item:hover {
  background-color: transparent !important;
}

/* Hide dropdown arrow with multiple approaches */
.no-dropdown-icon :deep(.v-input__append) {
  display: none !important;
}

.no-dropdown-icon :deep(.v-field__append-outer) {
  display: none !important;
}

.no-dropdown-icon :deep(.v-autocomplete .v-input__append) {
  display: none !important;
}

.no-dropdown-icon :deep(.v-select__append) {
  display: none !important;
}

/* Target specific icon classes */
.no-dropdown-icon :deep(.mdi-menu-down) {
  display: none !important;
}

.no-dropdown-icon :deep(.mdi-chevron-down) {
  display: none !important;
}

/* Ensure search icon stays visible */
.no-dropdown-icon :deep(.v-field__append-inner) {
  display: flex !important;
}

.no-dropdown-icon :deep(.v-field__append-inner .mdi-magnify) {
  display: block !important;
}

/* Loading Indicator */
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.loading-text {
  font-size: 14px;
  color: #666;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
}

.loading-assignments {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  margin-bottom: 16px;
}

/* Field Error Styles */
.field-error :deep(.v-field) {
  border-color: #f44336 !important;
}

.field-error :deep(.v-field--focused) {
  border-color: #f44336 !important;
}

.error-message {
  color: #f44336;
  font-size: 14px;
  margin-top: 4px;
  font-weight: 400;
}

/* Select Field Styling */
.property-followup-wrapper :deep(.v-select) {
  font-size: 14px;
}

/* Autocomplete Field Styling */
.property-followup-wrapper :deep(.v-autocomplete) {
  font-size: 14px;
}

.property-followup-wrapper :deep(.v-autocomplete .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  line-height: 48px;
  text-align: left;
}

.property-followup-wrapper :deep(.v-select .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 16px;
  line-height: 48px;
  text-align: left;
}

.property-followup-wrapper :deep(.v-select .v-field__input::placeholder) {
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
}

/* Direct targeting of Vuetify select field components */
.property-followup-wrapper :deep(.v-select .v-field) {
  height: 48px;
  display: flex;
  align-items: center;
}

.property-followup-wrapper :deep(.v-select .v-field__field) {
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0;
}

.property-followup-wrapper :deep(.v-select .v-field-label) {
  line-height: 1;
  top: 50%;
  transform: translateY(-50%);
}

/* Target the actual select input area */
.property-followup-wrapper :deep(.v-select .v-select__selection-text) {
  line-height: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  text-align: left;
}

/* Ensure placeholder appears centered */
.property-followup-wrapper :deep(.v-select .v-select__selection) {
  height: 48px;
  display: flex;
  align-items: center;
}

/* Text Field Styling */
.property-followup-wrapper :deep(.v-text-field) {
  font-size: 14px;
}

.property-followup-wrapper :deep(.v-text-field .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  padding: 0 16px;
}

.property-followup-wrapper :deep(.v-text-field .v-field__input::placeholder) {
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 400;
  height: 48px;
  text-align: left;
}

/* Textarea Styling - Match Route to Admin */
.property-followup-wrapper :deep(.v-textarea) {
  font-size: 14px;
}

.property-followup-wrapper :deep(.v-textarea .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
}

.property-followup-wrapper :deep(.v-textarea .v-field__input::placeholder) {
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 400;
}

/* Consistent Border Styling for All Form Fields */
.property-followup-wrapper :deep(.v-field) {
  border: 1px solid #5a5a5a !important;
  border-radius: 4px !important;
}

.property-followup-wrapper :deep(.v-field--focused) {
  border: 1px solid #5a5a5a !important;
  box-shadow: none !important;
}

.property-followup-wrapper :deep(.v-field--error) {
  border: 1px solid #f44336 !important;
}

.property-followup-wrapper :deep(.v-field__outline) {
  display: none !important;
}

/* Date Component Border */
.property-followup-wrapper :deep(.date-component .floating-input-container input) {
  border: 1px solid #5a5a5a !important;
  border-radius: 4px !important;
}

.property-followup-wrapper :deep(.date-component .floating-input-container input:focus) {
  border: 1px solid #5a5a5a !important;
  box-shadow: none !important;
}

/* Time Picker Component Border */
.property-followup-wrapper :deep(.time-picker-component .floating-input-container input) {
  border: 1px solid #5a5a5a !important;
  border-radius: 4px !important;
}

.property-followup-wrapper
  :deep(.time-picker-component .floating-input-container input:focus) {
  border: 1px solid #5a5a5a !important;
  box-shadow: none !important;
}

/* Autocomplete Border */
.property-followup-wrapper :deep(.v-autocomplete .v-field) {
  border: 1px solid #5a5a5a !important;
  border-radius: 4px !important;
}

/* Property Contact Fields - Reduce gap between First Name and Last Name */
.property-followup-wrapper .form-field .v-row {
  margin: 0 !important;
  gap: 8px !important;
}

.property-followup-wrapper .form-field .v-col {
  padding: 0 !important;
}

/* Section Header */
.section-header {
  font-size: 16px;
  font-weight: 400;
  color: #000;
  margin-bottom: 12px;
  display: block;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
}

/* Date and Time Fields */
/* Date and Time Fields - Figma Design Layout */
.property-followup-wrapper .date-time-container {
  display: flex !important;
  align-items: flex-end !important;
  gap: 12px !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  margin-bottom: 4px;
  margin-top: 12px !important;
  overflow: visible !important; /* Allow floating label to render above input */
}

.property-followup-wrapper .date-wrapper {
  flex: 1 !important;
  position: relative !important;
  max-width: 45% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.property-followup-wrapper .at-label-wrapper {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding-bottom: 32px !important;
  min-width: 24px !important;
  max-width: 40px !important;
  flex-shrink: 0 !important;
}

.property-followup-wrapper .time-wrapper {
  flex: 1 !important;
  position: relative !important;
  max-width: 45% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.time-input-row {
  display: flex !important;
  align-items: flex-end !important;
  gap: 8px !important;
  width: 100% !important;
  height: 100% !important;
}

.time-label-inline {
  font-size: 16px;
  font-weight: 400;
  color: #000;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  text-align: center !important;
  margin: 0 !important;
  padding: 0 !important;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
  line-height: 1.2;
  display: block !important;
}

.time-label {
  font-size: 14px;
  font-weight: 500;
  color: #000;
  margin-bottom: 8px;
  display: block;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
}

.time-field-input {
  position: relative;
  top: -8px !important;
  flex: 1 !important;
  min-width: 0 !important;
  height: 52px !important;
}

.property-followup-wrapper :deep(.time-field-input .v-field__input) {
  font-size: 14px;
  color: #000;
  font-weight: 400;
}

.property-followup-wrapper :deep(.time-field-input .v-field__input::placeholder) {
  color: #1a1a1a;
  font-size: 14px;
  font-weight: 400;
}

/* DateComponent styling override */
.property-followup-wrapper .date-wrapper :deep(.date-component) {
  width: 100% !important;
  display: block !important;
}

.property-followup-wrapper .date-wrapper :deep(.date-input) {
  width: 100% !important;
  padding: 8px !important;
  border: 1px solid #5a5a5a !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  background: transparent !important;
  transition: border-color 0.3s ease !important;
  box-sizing: border-box !important;
  cursor: pointer !important;
  height: 48px !important;
  text-align: left !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  padding-left: 12px !important;
}

.property-followup-wrapper .date-wrapper :deep(.floating-label) {
  position: absolute !important;
  left: 8px !important;
  top: 12px !important;
  font-size: 14px !important;
  color: #000000 !important;
  transition: all 0.3s ease !important;
  pointer-events: none !important;
  background: white !important;
  padding: 0 4px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  margin: 0 !important;
  z-index: 2 !important;
}

/* Date floating label when value is selected - match Time exactly */
.property-followup-wrapper
  .date-wrapper
  :deep(.floating-input-container .floating-label.float) {
  top: -12px !important;
  font-size: 14px !important;
  color: #000 !important;
  transform: none !important;
  background: #ffffff !important;
  padding: 0 4px !important;
  border-radius: 0px !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  line-height: normal !important;
  left: 8px !important;
  position: absolute !important;
  z-index: 10 !important;
}

/* Time floating label base state to match date */
.property-followup-wrapper .time-wrapper :deep(.floating-label) {
  font-size: 14px !important;
  color: #000 !important;
  left: 8px !important;
  top: 12px !important;
  transform: none !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  background: white !important;
  padding: 0 4px !important;
  margin: 0 !important;
  pointer-events: none !important;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1) !important;
  z-index: 2 !important;
  position: absolute !important;
  font-family: "Segoe UI Web (West European)", "Segoe UI", -apple-system,
    BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif !important;
}

/* Time floating label when value is selected - prevent cutting */
.property-followup-wrapper
  .time-wrapper
  :deep(.floating-input-container .floating-label.float) {
  top: -12px !important;
  transform: none !important;
  font-size: 14px !important;
  background: #ffffff !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  letter-spacing: 0.25px !important;
  box-shadow: none !important;
  line-height: 1.2 !important;
  left: 8px !important;
  position: absolute !important;
  z-index: 10 !important;
  font-family: "Segoe UI Web (West European)", "Segoe UI", -apple-system,
    BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif !important;
  color: #000 !important;
}

/* Ensure containers have enough top margin for floating labels */
.property-followup-wrapper .date-wrapper :deep(.floating-input-container) {
  overflow: visible !important;
  margin-top: 8px !important;
}

.property-followup-wrapper .time-wrapper :deep(.floating-input-container) {
  overflow: visible !important;
  margin-top: 8px !important;
}

/* Ensure no Vuetify overrides break the layout */
.property-followup-wrapper .date-time-container > * {
  flex-shrink: 0 !important;
}

.property-followup-wrapper .time-wrapper .v-text-field {
  width: 100% !important;
}

/* Error handling for select fields */
.property-followup-wrapper :deep(.v-select.v-input--error .v-field) {
  border-color: #f44336 !important;
}

.property-followup-wrapper :deep(.v-select.v-input--error .v-field__outline) {
  color: #f44336 !important;
}

.property-followup-wrapper :deep(.v-select.v-input--error .v-field__outline__start),
.property-followup-wrapper :deep(.v-select.v-input--error .v-field__outline__notch),
.property-followup-wrapper :deep(.v-select.v-input--error .v-field__outline__end) {
  border-color: #f44336 !important;
}

/* Error handling for text fields */
.property-followup-wrapper :deep(.v-text-field.v-input--error .v-field) {
  border-color: #f44336 !important;
}

.property-followup-wrapper :deep(.v-text-field.v-input--error .v-field__outline) {
  color: #f44336 !important;
}

.property-followup-wrapper :deep(.v-text-field.v-input--error .v-field__outline__start),
.property-followup-wrapper :deep(.v-text-field.v-input--error .v-field__outline__notch),
.property-followup-wrapper :deep(.v-text-field.v-input--error .v-field__outline__end) {
  border-color: #f44336 !important;
}

/* Force flex layout at all screen sizes above 351px */
@media (min-width: 351px) {
  .property-followup-wrapper .date-time-container {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-end !important;
    gap: 8px !important;
  }

  .property-followup-wrapper .date-wrapper {
    flex: 0 0 54% !important;
    min-width: 0 !important;
  }

  .property-followup-wrapper .time-wrapper {
    flex: 0 0 43% !important;
    min-width: 0 !important;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .date-wrapper {
    min-width: 180px !important;
  }

  .time-wrapper {
    flex: 0 0 130px !important;
    min-width: 100px !important;
  }
}

@media (max-width: 480px) {
  .property-followup-wrapper {
    font-size: 14px;
  }

  .field-label {
    font-size: 16px;
  }

  .form-field {
    margin-bottom: 12px;
  }

  .date-time-row {
    gap: 12px;
  }

  .property-followup-wrapper .date-wrapper :deep(.floating-label) {
    font-size: 14px !important;
    min-width: 70px !important;
    max-width: 100px !important;
    left: 8px !important;
  }
}

@media (max-width: 480px) {
  .action-buttons {
    justify-content: center;
  }

  .action-buttons :deep(.v-btn) {
    min-width: 120px;
    font-size: 14px;
  }
}

/* Handle clumsy layout between 321px - 350px */
@media (max-width: 350px) {
  .property-followup-wrapper .date-time-container {
    flex-direction: column !important;
    gap: 12px !important;
    align-items: stretch !important;
  }

  .property-followup-wrapper .date-wrapper {
    flex: none !important;
    min-width: none !important;
    max-width: none !important;
  }

  .property-followup-wrapper .time-wrapper {
    flex: none !important;
    min-width: none !important;
    max-width: none !important;
  }

  .property-followup-wrapper .at-label-wrapper {
    padding-bottom: 0px !important;
  }
}

@media (max-width: 320px) {
  .property-followup-wrapper .date-time-container {
    flex-direction: column !important;
    gap: 16px !important;
    align-items: stretch !important;
  }

  .property-followup-wrapper .date-wrapper {
    min-width: none !important;
    max-width: none !important;
    flex: none !important;
  }

  .property-followup-wrapper .time-wrapper {
    flex: none !important;
    min-width: none !important;
    max-width: none !important;
  }

  .section-header {
    margin-bottom: 8px;
  }

  .time-label {
    font-size: 14px;
  }

  .property-followup-wrapper .date-wrapper :deep(.floating-label) {
    font-size: 14px !important;
    min-width: 60px !important;
    max-width: 80px !important;
    transform: none !important;
    left: 8px !important;
  }

  .action-buttons {
    padding-top: 16px;
  }

  .action-buttons :deep(.v-btn) {
    width: 100%;
    min-width: auto;
  }

  .property-followup-wrapper .at-label-wrapper {
    padding-bottom: 0px !important;
  }
}

/* Reply to Selected Email Checkbox Styling */
.reply-checkbox-section {
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 0;
}

.reply-checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  margin-left: -8px;
}

.reply-checkbox {
  margin-left: 0;
  margin-right: 0;
}

.checkbox-main-label {
  margin-bottom: 0 !important;
  flex: 1;
  cursor: pointer;
  color: #000;
  font-size: 16px;
  font-weight: 400;
  user-select: none;
}

.checkbox-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  padding: 0;
  margin-left: 0; /* Align description to start at the same position as checkbox */
}

.reply-checkbox {
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.reply-checkbox :deep(.v-input__control) {
  min-height: auto;
}

.reply-checkbox :deep(.v-input__details) {
  display: none;
}

.reply-checkbox :deep(.v-selection-control) {
  align-items: flex-start;
  min-height: auto;
}

.reply-checkbox :deep(.v-selection-control__wrapper) {
  height: 20px;
}

/* Signature Field Styling */
.signature-container {
  position: relative;
  width: 100%;
}

.signature-vue-editor {
  width: 100%;
  border: 1px solid #003f2d;
  border-radius: 4px;
  background: white;
  min-height: 120px;
}

.signature-vue-editor :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f8f8;
  padding: 8px;
}

.signature-vue-editor :deep(.ql-container) {
  border: none;
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
    Helvetica Neue, sans-serif;
  font-size: 14px;
  min-height: 80px;
}

.signature-vue-editor :deep(.ql-editor) {
  min-height: 80px;
  padding: 12px;
}

.signature-vue-editor :deep(.ql-editor.ql-blank::before) {
  color: #999;
  font-style: normal;
  font-size: 14px;
}

.signature-char-count {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  padding-right: 4px;
}

.signature-char-count.error {
  color: #f44336;
  font-weight: 500;
}

.error-message {
  color: #f44336;
  font-size: 14px;
  margin-top: 4px;
  font-weight: 400;
}
</style>
